import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/seo";
import { 
  TrendingUp, Target, Users, Shield, Zap, Globe,
  ArrowRight, Building2, Award, Heart
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";

const values = [
  {
    icon: Target,
    title: "Accessibility",
    description: "We believe powerful trading tools should be available to everyone, not just Wall Street institutions.",
  },
  {
    icon: Shield,
    title: "Security First",
    description: "Your funds and data are protected with bank-grade encryption and industry-leading security practices.",
  },
  {
    icon: Zap,
    title: "Innovation",
    description: "We continuously improve our platform with cutting-edge technology to give you an edge in the markets.",
  },
  {
    icon: Heart,
    title: "Customer Focus",
    description: "Our success is measured by your success. We're committed to helping you achieve your trading goals.",
  },
];

const stats = [
  { value: "50K+", label: "Active Traders" },
  { value: "$2B+", label: "Volume Traded" },
  { value: "99.9%", label: "Uptime" },
  { value: "24/7", label: "Support" },
];

const timeline = [
  { year: "2020", title: "Founded", description: "TradePro was founded with a mission to democratize algorithmic trading." },
  { year: "2021", title: "Platform Launch", description: "Launched our no-code trading rule builder and paper trading features." },
  { year: "2022", title: "Multi-Asset Support", description: "Expanded to support stocks, options, and cryptocurrency trading." },
  { year: "2023", title: "Event Triggers", description: "Introduced news and social media event-driven trading automation." },
  { year: "2024", title: "Enterprise Launch", description: "Launched enterprise solutions for institutions and professional traders." },
  { year: "2026", title: "Global Expansion", description: "Expanded broker integrations and international market access." },
];

export default function AboutPage() {
  const { isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="About - TradePro | Democratizing Algorithmic Trading"
        description="Learn about TradePro's mission to make algorithmic trading accessible to everyone. 50K+ traders, $2B+ volume, and 99.9% platform uptime."
      />
      <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" data-testid="link-home-logo">
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">TradePro</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6" data-testid="nav-main">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-features">Features</Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-pricing">Pricing</Link>
            <Link href="/about" className="text-sm text-foreground font-medium" data-testid="link-nav-about">About</Link>
          </nav>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link href="/dashboard">
                <Button data-testid="button-go-to-dashboard">Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <Button variant="ghost" data-testid="button-login" onClick={() => setLocation("/login")}>Log in</Button>
                <Button data-testid="button-get-started" onClick={() => setLocation("/login")}>Get Started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="pt-24 pb-20">
        <section className="px-4 mb-16">
          <div className="container mx-auto text-center max-w-3xl">
            <Badge variant="secondary" className="mb-4">About TradePro</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Democratizing Algorithmic Trading</h1>
            <p className="text-xl text-muted-foreground">
              We're on a mission to give every trader access to the same powerful automated trading 
              tools that were once reserved for institutions and hedge funds.
            </p>
          </div>
        </section>

        <section className="px-4 py-12 bg-muted/30 mb-16">
          <div className="container mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="text-3xl md:text-4xl font-bold text-primary">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 mb-20">
          <div className="container mx-auto max-w-4xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Story</h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    TradePro was founded by a team of traders and engineers who saw a fundamental 
                    inequality in the markets. Large institutions had access to sophisticated 
                    algorithmic trading systems, while individual traders were left behind.
                  </p>
                  <p>
                    We set out to change that. Our mission is to democratize algorithmic trading 
                    by making powerful automation tools accessible to traders of all skill levels—
                    without requiring a computer science degree or millions in capital.
                  </p>
                  <p>
                    Today, TradePro serves thousands of traders worldwide, from beginners learning 
                    the basics to professional traders managing large portfolios. Our platform 
                    processes millions of trades annually across stocks, options, and cryptocurrencies.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl flex items-center justify-center">
                  <Building2 className="h-24 w-24 text-primary/40" />
                </div>
                <Badge className="absolute -bottom-3 -right-3">
                  <Award className="h-3 w-3 mr-1" />
                  SEC Registered
                </Badge>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-20 bg-muted/30">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Values</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                The principles that guide everything we do
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value) => (
                <Card key={value.title} className="text-center" data-testid={`value-${value.title.toLowerCase()}`}>
                  <CardHeader>
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                      <value.icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{value.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Journey</h2>
              <p className="text-muted-foreground">
                Key milestones in our mission to democratize trading
              </p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border" />
              <div className="space-y-8">
                {timeline.map((event, index) => (
                  <div 
                    key={event.year} 
                    className={`relative pl-12 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:ml-auto md:pl-12'}`}
                    data-testid={`timeline-${event.year}`}
                  >
                    <div className={`absolute left-0 md:left-auto ${index % 2 === 0 ? 'md:right-0 md:-mr-6' : 'md:-ml-6'} top-1 h-8 w-8 rounded-full bg-primary flex items-center justify-center`}>
                      <span className="text-xs font-bold text-primary-foreground">{event.year.slice(-2)}</span>
                    </div>
                    <div className={index % 2 === 0 ? 'md:mr-6' : 'md:ml-6'}>
                      <Badge variant="outline" className="mb-2">{event.year}</Badge>
                      <h3 className="font-semibold">{event.title}</h3>
                      <p className="text-sm text-muted-foreground">{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto text-center max-w-2xl">
            <Globe className="h-12 w-12 mx-auto mb-6 text-primary" />
            <h2 className="text-3xl font-bold mb-4">Join Our Community</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Thousands of traders are already using TradePro to automate their strategies. 
              Join them today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/open-account">
                <Button size="lg" data-testid="button-open-account">
                  Open New Account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link href="/features">
                <Button size="lg" variant="outline" data-testid="button-explore-features">
                  Explore Features
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t py-8 px-4">
        <div className="container mx-auto text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} TradePro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
